/**
 * JsonLd — audit remediation Phase 2.
 *
 * The SEO audit required proper structured data across the domain
 * (Organization, WebSite, BreadcrumbList, Article, Recipe, Product,
 * LocalBusiness, Event). This tiny helper emits a `<script
 * type="application/ld+json">` tag via react-helmet-async so any page
 * can drop richly-typed schema without pulling in a JSON-LD library.
 *
 * Usage:
 *   <JsonLd data={{ "@context":"https://schema.org", "@type":"Article", ... }} />
 *   <JsonLd data={[orgSchema, breadcrumbSchema]} />  // multiple docs
 */
import { Helmet } from "react-helmet-async";

export default function JsonLd({ data }) {
  if (!data) return null;
  const docs = Array.isArray(data) ? data : [data];
  return (
    <Helmet>
      {docs.map((doc, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(doc)}
        </script>
      ))}
    </Helmet>
  );
}

// ---------------------------------------------------------------------------
// Prebuilt schema helpers — the audit calls these out explicitly as the
// minimum viable schema pack for a food knowledge platform.
// ---------------------------------------------------------------------------
export function foodPulseOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "FoodPulse",
    "url": "https://foodpulse.net",
    "logo": "https://foodpulse.net/foodpulse-icon-192.png",
    "description":
      "FoodPulse connects growers, food businesses, cooks, charities and neighbors " +
      "to discover food, share surplus, reduce waste, and improve access to nutritious food.",
    "sameAs": ["https://socialpulse.com", "https://farmerspulse.com"],
  };
}

export function foodPulseWebsiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "FoodPulse",
    "url": "https://foodpulse.net",
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((it, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": it.name,
      "item": it.url,
    })),
  };
}
